import React, { useEffect, useState } from "react";
import Navigation from "../../components/Navigation";
import Y_Us_StripComponent from "../common/Y_us_strip";
import { Layer, Stage } from "react-konva";
import { CustomImage } from "./CheckOutPage";
import Constants from "../Variables/Constants";
import axios from "axios";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import moment from "moment";
import { Link, useNavigate } from "react-router-dom";

const parseJsonValue = (value, fallback = null) => {
  if (!value) {
    return fallback;
  }

  if (typeof value === "object") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const getDisplayName = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  return (
    value.name ||
    value.title ||
    value.size_name ||
    value.color_name ||
    value.label ||
    value.code ||
    ""
  );
};

const getColorBackground = (color) => {
  if (!color) {
    return "";
  }

  if (Array.isArray(color)) {
    return color.length > 1
      ? `linear-gradient(90deg, ${color.join(", ")})`
      : color[0];
  }

  return color;
};

const OptionRow = ({ label, value, color }) => {
  if (!value && !color) {
    return null;
  }

  const background = getColorBackground(color);

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <span className="fw-bold">{label}: </span>
      {background && (
        <span
          className="d-inline-block rounded-circle border"
          style={{ width: "16px", height: "16px", background }}
        />
      )}
      <span>{value || background}</span>
    </div>
  );
};

const getOrderItemSelections = (orderItem) => {
  const customizeImage = orderItem?.product?.customize_image || {};
  const designData = parseJsonValue(
    customizeImage?.design_data || orderItem?.design_data,
    {},
  );
  const rectangles = Array.isArray(designData?.rectangles)
    ? designData.rectangles
    : [];
  const art1Thread = rectangles.find(
    (item) => item?.type === "art1" && item?.currentSelectedThread,
  )?.currentSelectedThread;
  const art2Threads = rectangles
    .filter((item) => item?.type === "art2" && item?.currentSelectedThread)
    .map((item) => ({
      name: item?.name,
      thread: item.currentSelectedThread,
    }));
  const textObjects = Array.isArray(designData?.textObjects)
    ? designData.textObjects.filter((text) => text?.text)
    : [];
  const addOns =
    orderItem?.addon_details ||
    designData?.addOns ||
    orderItem?.selectedAddons ||
    [];

  return {
    customizeImage,
    designData,
    mainFabric: customizeImage?.main_fabric_id || orderItem?.product?.main_fabric,
    accentFabric: customizeImage?.accent_color_id,
    size: customizeImage?.size_id || orderItem?.product?.size,
    art1Design: designData?.currentArt1Design,
    art2Designs: Array.isArray(designData?.currentArt2Design)
      ? designData.currentArt2Design
      : designData?.currentArt2Design
        ? [designData.currentArt2Design]
        : [],
    art1Thread,
    art2Threads,
    textObjects,
    addOns: Array.isArray(addOns) ? addOns : [addOns],
    notes: designData?.notes || orderItem?.notes,
  };
};

const OrderItemSelections = ({ orderItem }) => {
  const selections = getOrderItemSelections(orderItem);

  return (
    <div className="mt-2 fs-14">
      <OptionRow label="Size" value={getDisplayName(selections.size)} />
      <OptionRow label="Main Fabric" value={getDisplayName(selections.mainFabric)} />
      <OptionRow
        label="Accent Fabric / Color"
        value={getDisplayName(selections.accentFabric)}
      />
      <OptionRow label="Art 1 Design" value={getDisplayName(selections.art1Design)} />
      <OptionRow
        label="Art 1 Thread Color"
        value={getDisplayName(selections.art1Thread)}
        color={selections.art1Thread?.color}
      />
      {selections.art2Designs.map((design, index) => (
        <OptionRow
          key={`art2-design-${index}`}
          label="Graphic Add-on"
          value={getDisplayName(design)}
        />
      ))}
      {selections.art2Threads.map((item, index) => (
        <OptionRow
          key={`art2-thread-${index}`}
          label={`${item.name || "Graphic Add-on"} Thread Color`}
          value={getDisplayName(item.thread)}
          color={item.thread?.color}
        />
      ))}
      {selections.textObjects.map((text, index) => (
        <OptionRow
          key={`text-${index}`}
          label={`Personalized Text ${index + 1}`}
          value={`${text.text}${text.color_name ? ` (${text.color_name})` : ""}`}
          color={text.color}
        />
      ))}
      {selections.addOns.map((addon, index) => (
        <OptionRow
          key={`addon-${index}`}
          label="Add-on"
          value={`${addon?.title ? `${addon.title}: ` : ""}${getDisplayName(addon)}${addon?.price ? ` - $${addon.price}` : ""}`}
        />
      ))}
      <OptionRow label="Notes" value={selections.notes} />
    </div>
  );
};

const Track = () => {
  const id = window.location.search.split("=").at(-1);
  const [orderData, setOrderData] = useState("");
  const [upsTracking, setUpsTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [flag, setFlag] = useState(false);
  const navigate = useNavigate();

  const handleGetOrderDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${Constants.getUrls.getOrder}/${id}`);
      if (res.data.status === "success") {
        setOrderData(res.data.data);
        setLoading(false);
      } else {
        setLoading(false);
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(res.data.message);
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
      toast.clearWaitingQueue();
      toast.dismiss();
      toast.error("Failed to fetch order details.", error.message);
    }
  };

  const handleGetUpsTracking = async () => {
    try {
      const res = await axios.get(`${Constants.getUrls.upsOrderTrack}/${id}/track`);
      if (res.data.status === "success") {
        setUpsTracking(res.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (id) {
      handleGetOrderDetails();
      handleGetUpsTracking();
    }
  }, [id, flag]);

  // const steps = [
  //   {
  //     time: "11:20",
  //     date: "Nov 19",
  //     status: "Order Received",
  //     description: "Your order is being processed in Dubai.",
  //     completed: true,
  //   },
  //   {
  //     time: "14:00",
  //     date: "Nov 17",
  //     status: "Shipped",
  //     description: "Order is shipped and on the way.",
  //     completed: true,
  //   },
  //   {
  //     time: "18:30",
  //     date: "Nov 18",
  //     status: "Out for Delivery",
  //     description: "Your order is out for delivery.",
  //     completed: true,
  //   },
  //   {
  //     time: "10:00",
  //     date: "Nov 19",
  //     status: "Delivered",
  //     description: "Your order has been successfully delivered.",
  //     completed: true,
  //   },
  // ];

  if (loading) {
    return (
      <>
        <center>
          <Loader />
        </center>
      </>
    );
  }

  const cancelOrderValidationStatus = [
    "Confirmed",
    "Processing",
    "On Hold"
  ]
  const canShowPayNow =
    Number(orderData?.amount_due_now || 0) > 0 &&
    (orderData?.payment_status === "Payment Pending" ||
      orderData?.payment_status === "Paid a 50% Deposit");
  console.log('orderData', orderData)
  const upsPackage = upsTracking?.data?.trackResponse?.shipment?.[0]?.package?.[0];
  const upsActivities = upsPackage?.activity || [];
  const estimatedDeliveryDate =
    upsTracking?.estimated_delivery_date || orderData?.estimated_delivery_date;
  const estimatedDeliveryTime =
    upsTracking?.estimated_delivery_time || orderData?.estimated_delivery_time;
  const estimatedDeliveryService =
    upsTracking?.estimated_delivery_service || orderData?.estimated_delivery_service;
  const formatUpsActivityDate = (activity) => {
    if (!activity?.date) return "";
    const dateTime = `${activity.date}${activity.time || "000000"}`;
    return moment(dateTime, "YYYYMMDDHHmmss").format("DD-MMM-YYYY h:mm A");
  };
  const formatEstimatedDeliveryTime = (date, time) => {
    if (!date && !time) return "";

    const normalizedTime = time
      ? String(time).replace(/\D/g, "").padEnd(6, "0").slice(0, 6)
      : "";
    const datePart = date
      ? moment(date, ["YYYYMMDD", "YYYY-MM-DD", moment.ISO_8601], true)
      : null;

    if (datePart?.isValid() && normalizedTime) {
      const estimatedAt = moment(`${datePart.format("YYYYMMDD")}${normalizedTime}`, "YYYYMMDDHHmmss");
      return estimatedAt.isValid()
        ? estimatedAt.format("DD-MMM-YYYY h:mm A")
        : `${datePart.format("DD-MMM-YYYY")} ${time}`;
    }

    if (datePart?.isValid()) {
      return datePart.format("DD-MMM-YYYY");
    }

    if (normalizedTime) {
      const estimatedAt = moment(`${moment().format("YYYYMMDD")}${normalizedTime}`, "YYYYMMDDHHmmss");
      return estimatedAt.isValid() ? estimatedAt.format("h:mm A") : time;
    }

    return "";
  };
  const estimatedDeliveryLabel = formatEstimatedDeliveryTime(
    estimatedDeliveryDate,
    estimatedDeliveryTime
  );
  const handleCancelOrder = async () => {
    const url = Constants.postUrls.refundOrder;
    try {
      const response = await axios.post(url,
        { sessionId: orderData.session_id },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      );
      console.log("response", response);
      toast.clearWaitingQueue();
      toast.dismiss();
      toast.success("Your order is refunded successfully!");
      setFlag(!flag);
      // if (response.status === 200) {
      //   toast.clearWaitingQueue();
      //   toast.dismiss();
      //   toast.success("Password Changed Successfully!");
      //   navigate("/profile")
      // } else {
      //   toast.clearWaitingQueue();
      //   toast.dismiss();
      //   toast.error("Something went wrong!");
      // }
    } catch (error) {
      toast.clearWaitingQueue();
      toast.dismiss();
      toast.error(`Something went wrong!, ${error.message}`);
      setFlag(!flag);
    }
  }

  const handlePayNow = async () => {
    try {
      const response = await axios.post(
        `${Constants.postUrls.payNowOrder}${orderData?._id}`,
        {},
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      );
      if (response?.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(response?.data?.message || "Unable to start payment.");
      }
    } catch (error) {
      toast.clearWaitingQueue();
      toast.dismiss();
      toast.error(error?.response?.data?.message || error.message);
    }
  };

  return (
    <>
      <Navigation />

      <div className="container f mt-4">
        <div className="row h-fit">
          <div className="col-xl-6 col-12">
            <div className="d-flex justify-content-between align-items-center">
              <p className="fs-22">Shipment details</p>
              <div>
                <button type="button" disabled className="text-white rounded-4 btn-primary fs-14 me-3 mb-2" style={{ backgroundColor: "grey" }}>
                  {orderData?.order_status}
                </button>
                {cancelOrderValidationStatus.includes(orderData?.order_status) && (
                  <button type="button" className="text-white rounded-4 btn-primary fs-14 bg-blue" onClick={() => handleCancelOrder()}>
                    Cencel Order
                  </button>
                )}
                {canShowPayNow && (
                  <button
                    type="button"
                    className="text-white rounded-4 btn-primary fs-14 bg-blue ms-2"
                    onClick={() => handlePayNow()}
                  >
                    Pay Now 
                  </button>
                )}
              </div>
            </div>
            <div className="border d-flex p-2 py-5 align-items-center justify-content-center flex-column rounded-4 position-relative">
              {orderData?.order_status_history && Array.isArray(orderData?.order_status_history) && orderData?.order_status_history.length > 0 && orderData?.order_status_history.map((step, index) => (
                <div
                  key={index}
                  className="d-flex fs-16 w-100 align-items-start mb-3"
                >
                  {/* Left Side (Time & Date) */}
                  <div className="text-end py-4 pe-3">
                    <p className="mb-0 ">{moment(step.date).format("h:mm A")}</p>
                    <p className="mb-0 text-muted fs-12">{moment(step.date).format("DD-MMM-YYYY")}</p>
                  </div>

                  {/* Timeline Dots and Line */}
                  <div className="position-relative w-ccc">
                    {/* Timeline Dot */}

                    {/* Status Indicator */}
                    <div
                      className={`rounded-circle ${step._id ? "bg-success" : "bg-gray"
                        }`}
                      style={{ width: "15px", height: "15px", zIndex: 999999 }}
                    >
                      {/* Timeline Line */}
                    </div>

                    {/* Timeline Line */}

                    <div
                      className="position-absolute top-2 start-50 "
                      style={{
                        width: "4px",
                        height: "73px",
                        backgroundColor: step._id ? "#12B76A" : "#D4D7E3",
                        zIndex: -1,
                      }}
                    ></div>
                  </div>

                  {/* Right Side (Status Details) */}
                  <div className="py-4 ps-3">
                    <h6 className="fs-14">{step.status}</h6>
                    <p className="text-muted text-nowrap fs-12">
                      {/* {step.description} */}
                      Your order status is {step.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {upsTracking?.tracking_number && (
              <div className="border rounded-4 p-4 mt-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <p className="fs-16 fw-bold mb-1">UPS Tracking</p>
                    <p className="text-muted fs-12 mb-0">{upsTracking.tracking_number}</p>
                  </div>
                  <div className="text-end">
                    <span className="fs-14 d-block">{upsPackage?.currentStatus?.description || upsPackage?.statusDescription || "Tracking requested"}</span>
                    {estimatedDeliveryLabel && (
                      <span className="text-muted fs-12 d-block mt-1">
                        Estimated Time: {estimatedDeliveryLabel}
                      </span>
                    )}
                    {estimatedDeliveryService && (
                      <span className="text-muted fs-12 d-block">
                        {estimatedDeliveryService}
                      </span>
                    )}
                  </div>
                </div>
                {upsActivities.slice(0, 5).map((activity, index) => (
                  <div className="border-top pt-3 mt-3" key={`${activity.date}-${activity.time}-${index}`}>
                    <p className="fs-14 fw-semibold mb-1">{activity?.status?.description}</p>
                    <p className="text-muted fs-12 mb-0">
                      {formatUpsActivityDate(activity)}
                      {activity?.location?.address?.city ? ` - ${activity.location.address.city}` : ""}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="col-xl-6 col-12">
            <p className="fs-16 text-end mt-1 pt-4 pt-lg-0">
              {/* <b>Estimated Days </b>: 2-6 days */}
            </p>
            {estimatedDeliveryLabel && (
              <p className="fs-16 text-end mt-1 mb-0">
                <b>Estimated UPS Time </b>: {estimatedDeliveryLabel}
              </p>
            )}
            <div className="accordion mt-4" id="accordionExample">
              <div className="accordion-item rounded-4">
                {" "}
                <h2 className="accordion-header p-4">
                  <button
                    className="accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseOne"
                    aria-expanded="true"
                    aria-controls="collapseOne"
                  >
                    Orders Details
                  </button>
                </h2>
                <div
                  id="collapseOne"
                  className="accordion-collapse collapse show"
                  data-bs-parent="#accordionExample"
                >
                  <div className="accordion-body fs-14">
                    {orderData && Object.keys(orderData).length > 0 && orderData.items.length > 0 && (
                      <div className="" key={orderData._id}>
                        {orderData.items.map((orderItem) => {
                          const priceData = parseJsonValue(orderItem.price_data, {});
                          const matchingBag = parseJsonValue(orderItem.matching_bag, {});

                          return (
                            <Link
                              to={`/track?id=${orderData._id}`}
                              key={`${orderItem.product_id}-${orderData._id}`}
                              className="order-item-link"
                            >
                            <div className="order-item-card">
                              <div className="order-item-info">
                                {orderItem?.images &&
                                  Array.isArray(orderItem?.images) &&
                                  orderItem?.images?.length > 0 &&
                                  orderItem?.images[0] && (
                                    <img loading="lazy" 
                                      className="order-item-image"
                                      src={`${Constants.imgDomain}${orderItem?.images[0]}`}
                                      alt={orderItem.name}
                                    />
                                  )}
                                <div className="order-item-details">
                                  <h1 className="order-item-name">
                                    {orderItem.name}
                                  </h1>

                                  {orderItem?.price_data && (<>
                                    <div className="mt-2">
                                      <h4 className="my-0"><b>Fabric Price: </b>${priceData?.fabric_price || 0}</h4>

                                      <h4 className="my-0"><b>Art 1 Design Price: </b>${priceData?.art1_design_price || priceData?.design_price || 0}</h4>

                                      <h4 className="my-0"><b>Art 2 Design Price: </b>${priceData?.art2_design_price || 0}</h4>

                                      <h4 className="my-0"><b>Accent Color Price: </b>${priceData?.accent_price || 0}</h4>

                                      <h4 className="my-0"><b>Text Price: </b>${priceData?.text_price || 0}</h4>

                                      <h4 className="my-0"><b>Add-ons Price: </b>${priceData?.add_ons_price || 0}</h4>
                                    </div>
                                  </>)}

                                  <OrderItemSelections orderItem={orderItem} />

                                  {/* {orderItem?.matching_bag && orderItem?.matching_bag !== "[object Object]" && (<>
                                    {JSON.parse(orderItem.matching_bag)?.name && (<>
                                      <h4><b>Matching Bag </b>{JSON.parse(orderItem.matching_bag).name}</h4>
                                      <h4><b>Matching Bag Price: </b>${JSON.parse(orderItem.matching_bag).price}</h4>
                                    </>)}
                                  </>)} */}

                                  {orderItem?.matching_bag && orderItem?.matching_bag !== "[object Object]" && (<>
                                    {matchingBag?.name && (<>
                                      <h4 className="my-0"  ><b>Matching Bag </b>{matchingBag.name}</h4>
                                      <h4 className="my-0"><b>Matching Bag Price: </b>${matchingBag.price || 0}</h4>
                                    </>)}
                                  </>)}

                                  <h5>Price Per Quantity (${orderItem.price})</h5>
                                  <p className="order-item-quantity">
                                    Quantity: {orderItem.quantity}
                                  </p>
                                </div>
                              </div>
                              <div className="order-item-price">
                                <p className="order-item-total-price">
                                  ${orderItem.price * orderItem.quantity}
                                </p>
                              </div>
                            </div>
                            </Link>
                          );
                        })}
                        {/* <div className="d-flex justify-content-between flex-wrap w-100">
                          <div className="h3 me-2">
                            Return Eligible Till: {moment(orderData.return_date).format(
                              "DD-MMM-YYYY"
                            )}
                          </div>
                          <div className="h3">
                            Total Price: ${orderData.amount}
                          </div>
                        </div>
                        <div className="order-status-container">
                          <button
                            className="order-status-button"
                            type="button"
                            onClick={() => navigate(`/track?id=${orderData._id}`)}
                          >
                            {orderData?.order_status}
                          </button>
                        </div> */}
                        <div className="d-flex justify-content-between fs-16 mt-5 border-top px-4 pt-3">
                          <span>Order No</span>
                          <span>{orderData?.display_order_id || orderData?._id || "-"}</span>
                        </div>
                        <div className="d-flex justify-content-between fs-16 px-4 pt-2">
                          <span>Subtotal</span>
                          <span>${Number(orderData?.subtotal || 0).toFixed(2)}</span>
                        </div>
                        <div className="d-flex justify-content-between fs-16 px-4 pt-2">
                          <span>Shipping</span>
                          <span>${Number(orderData?.shipping_amount || 0).toFixed(2)}</span>
                        </div>
                        <div className="d-flex justify-content-between fs-16 px-4 pt-2">
                          <span>Tax</span>
                          <span>${Number(orderData?.tax_amount || 0).toFixed(2)}</span>
                        </div>
                        <div className="d-flex justify-content-between fs-16 px-4 pt-2">
                          <span>Discount</span>
                          <span>${Number(orderData?.discount_amount || 0).toFixed(2)}</span>
                        </div>
                        {(orderData?.ups_service_description || orderData?.ups_service_code) && (
                          <div className="d-flex justify-content-between fs-16 px-4 pt-2">
                            <span>UPS Service</span>
                            <span>
                              {orderData?.ups_service_description && orderData?.ups_service_code
                                ? `${orderData.ups_service_description} (${orderData.ups_service_code})`
                                : (orderData?.ups_service_description || orderData?.ups_service_code)}
                            </span>
                          </div>
                        )}
                        <div className="d-flex justify-content-between fs-16 px-4 pt-2">
                          <span>Due Now</span>
                          <span>${Number(orderData?.amount_due_now || 0).toFixed(2)}</span>
                        </div>
                        <div className="d-flex justify-content-between fs-20 border-top rounded-5 px-4 pt-3 total-amount mt-3">
                          <strong>Total</strong>
                          <strong>${Number(orderData?.amount || 0).toFixed(2)}</strong>
                        </div>
                      </div>
                    )}



                    {/* {orderData &&
                      orderData?.items &&
                      Array.isArray(orderData?.items) &&
                      orderData?.items?.length > 0 &&
                      orderData.items.map((orderItem) => (
                        <div className="d-flex border-bottom mb-4 pb-4 rounded-5 shadow-cc p-4  align-items-center justify-content-between">
                          <div className="d-flex align-items-start gap-4 ">
                            {orderItem?.images && Array.isArray(orderItem?.images) && orderItem?.images?.length > 0 && orderItem?.images[0] && (
                              <img loading="lazy"  style={{ width: "90px", height: "90px" }} src={`${Constants.imgDomain}${orderItem?.images[0]}`} alt="" />
                            )}
                            <div>
                              <p className="fs-22 fw-bold">{orderItem.name}</p>

                              <div className=" d-flex flex-column gap-2 mt-4">
                                <small className="fs-14 fw-bold">
                                  Quantity
                                </small>
                              </div>
                            </div>
                          </div>
                          <div className="d-flex flex-column gap-4 ">
                            <p className="fs-16 fw-500">${orderItem.price * orderItem.quantity}</p>{" "}
                            <p className="fs-16 fw-500">{orderItem.quantity}</p>
                          </div>
                        </div>
                      ))}
                    <div className="d-flex justify-content-between  fs-20 mt-5 border-top rounded-5 px-4 pt-3 total-amount mt-3">
                      <strong>Total</strong>
                      <strong>${orderData?.amount}</strong>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>

            {/* <div className="accordion mt-4" id="accordionExample">
              <div className="accordion-item rounded-4">
                {" "}
                <h2 className="accordion-header p-4">
                  <button
                    className="accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseOne"
                    aria-expanded="false"
                    aria-controls="collapseOne"
                  >
                    Orders Details
                  </button>
                </h2>
                <div
                  id="collapseOne"
                  className="accordion-collapse collapse "
                  data-bs-parent="#accordionExample"
                >
                  <div className="accordion-body fs-14">
                    {orderData &&
                      orderData?.items &&
                      Array.isArray(orderData?.items) &&
                      orderData?.items?.length > 0 &&
                      orderData.items.map((orderItem) => (
                        <div className="d-flex border-bottom mb-4 pb-4 rounded-5 shadow-cc p-4  align-items-center justify-content-between">
                          <div className="d-flex align-items-start gap-4 ">
                            {orderItem?.images && Array.isArray(orderItem?.images) && orderItem?.images?.length > 0 && orderItem?.images[0] && (
                              <img loading="lazy"  style={{ width: "90px", height: "90px" }} src={`${Constants.imgDomain}${orderItem?.images[0]}`} alt="" />
                            )}
                            <div>
                              <p className="fs-22 fw-bold">{orderItem.name}</p>

                              <div className=" d-flex flex-column gap-2 mt-4">
                                <small className="fs-14 fw-bold">
                                  Quantity
                                </small>
                              </div>
                            </div>
                          </div>
                          <div className="d-flex flex-column gap-4 ">
                            <p className="fs-16 fw-500">${orderItem.price * orderItem.quantity}</p>{" "}
                            <p className="fs-16 fw-500">{orderItem.quantity}</p>
                          </div>
                        </div>
                      ))}
                    <div className="d-flex justify-content-between  fs-20 mt-5 border-top rounded-5 px-4 pt-3 total-amount mt-3">
                      <strong>Total</strong>
                      <strong>${orderData?.amount}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      <div className="container">
        <Y_Us_StripComponent />
      </div>
    </>
  );
};

export default Track;

// {/* <div className="accordion-item rounded-4 mt-3">
//   <h2 className="accordion-header border-top p-4">
//     <button
//       className="accordion-button"
//       type="button"
//       data-bs-toggle="collapse"
//       data-bs-target="#collapseTwo"
//       aria-expanded="false"
//       aria-controls="collapseTwo"
//     >
//       Item Cancelled
//     </button>
//   </h2>
//   <div
//     id="collapseTwo"
//     className="accordion-collapse collapse "
//     data-bs-parent="#accordionExample"
//   >
//     <div className="accordion-body fs-14">
//       <div className="prodCart flex-wrap shadow-cc rounded-4  justify-content-between p-5 d-flex  gap-4">
//         <div className="d-flex gap-4">
//           <img loading="lazy" 
//             src="/prod2.png"
//             className="object-fit-cover"
//             alt=""
//           />
//           <div className="d-flex flex-column gap-3">
//             <div className="">
//               <h1 className="fs-22 fw-semibold">Shaarei Vilna</h1>
//               <p className="fs-16">
//                 Bar Mitzvah Bag (BMX 10.5" x 10")
//               </p>
//             </div>
//             <div className="">
//               <p className="fw-semibold fs-16">Matching Bag</p>
//               <p className="fs-16">
//                 1 Matching Tefilin Bag (TF 8.5 x 9.5)
//               </p>
//             </div>
//           </div>
//         </div>

//         <div className="d-flex justify-content-center flex-xl-column w-auto gap-5 gap-sm-4 align-items-center ">
//           <p className="fw-medium fs-16 fw-bold text-danger">
//             Cancelled
//           </p>
//         </div>
//       </div>

//       <p className="fs-14 fw-500 text-light-gray">
//         We're sorry to inform you that the item you've order went
//         out of stock so we've issued the refund of XXXX aed to
//         your preferred payment method.
//       </p>
//     </div>
//   </div>
// </div> */}
